<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import clubApplicationService from '../services/clubApplication.service'
import { useToastStore } from '../stores/toast'

const props = defineProps<{
  show: boolean
  clubUuid: string
  applicationUuids: string[]
  // Shown in the question when rejecting a single application
  name?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'done'): void
}>()

const { t } = useI18n()
const toastStore = useToastStore()

const submitting = ref(false)

const close = () => {
  if (!submitting.value) emit('close')
}

const handleReject = async () => {
  submitting.value = true
  try {
    const response = await clubApplicationService.reject(props.clubUuid, props.applicationUuids)
    toastStore.success(response.message || t('clubApplications.rejectedSuccess'))
    emit('done')
  } catch (err: any) {
    console.error('Reject applications error:', err)
    toastStore.error(err.response?.data?.message || t('clubApplications.errorRejecting'))
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="close"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('clubApplications.rejectConfirmTitle') }}</h2>
        <p class="text-gray-700 mb-6">
          <template v-if="name">
            {{ t('clubApplications.rejectConfirmOne') }} "<span class="font-semibold">{{ name }}</span>"?
          </template>
          <template v-else>
            {{ t('clubApplications.rejectConfirmMany', { count: applicationUuids.length }) }}
          </template>
        </p>

        <div class="flex gap-3 justify-end">
          <button
            @click="close"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="submitting"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleReject"
            class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="submitting || applicationUuids.length === 0"
          >
            {{ submitting ? t('clubApplications.rejecting') : t('clubApplications.reject') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
